import { Box, Stack, Text, UnstyledButton } from '@mantine/core';
import { useState } from 'react';
import type { FC } from 'react';
import { useNavigate } from 'react-router';

import { useLogin, useLogout, useMe } from '../../hooks/data/auth.ts';

const DEV_ACCOUNTS = [
  {
    label: 'Onboarded Promoter',
    email: 'development@example.com',
    password: '12345678',
    description: 'Full access, has events',
  },
  {
    label: 'Non-onboarded Promoter',
    email: 'onboarding@example.com',
    password: '12345678',
    description: 'Triggers onboarding flow',
  },
] as const;

const DevTriangle: FC<{ size?: number }> = ({ size = 20 }) => (
  <svg fill="none" height={size} viewBox="0 0 24 24" width={size} xmlns="http://www.w3.org/2000/svg">
    <path d="M2 22L12 4L22 22H2Z" fill="#00C853" />
  </svg>
);

const AccountButton: FC<{
  label: string;
  description: string;
  onClick: () => void;
}> = ({ label, description, onClick }) => (
  <UnstyledButton
    style={{
      padding: '10px 14px',
      borderRadius: 8,
      backgroundColor: 'rgba(255, 255, 255, 0.06)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      cursor: 'pointer',
      transition: 'background-color 150ms, border-color 150ms',
    }}
    onClick={onClick}
    onMouseEnter={(e) => {
      e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.12)';
      e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.06)';
      e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
    }}
  >
    <Stack gap={2}>
      <Text c="white" ff="monospace" fw={600} size="sm">
        {label}
      </Text>
      <Text c="rgba(255,255,255,0.5)" ff="monospace" size="xs">
        {description}
      </Text>
    </Stack>
  </UnstyledButton>
);

export const DevToolsPanel: FC = () => {
  const [opened, setOpened] = useState(false);
  const { data: user, isAuthError } = useMe();
  const { mutate: login } = useLogin({
    onSuccess: () => {
      globalThis.location.href = '/dashboard';
    },
  });
  const { mutate: logout } = useLogout();
  const navigate = useNavigate();

  if (!import.meta.env.DEV) {
    return null;
  }

  const isLoggedIn = !!user && !isAuthError;

  const handleLogin = (email: string, password: string) => {
    setOpened(false);
    login({ email, password });
  };

  const handleLogout = () => {
    setOpened(false);
    logout(undefined, {
      onSuccess: () => void navigate('/auth/login'),
    });
  };

  const handleSwitchAccount = (email: string, password: string) => {
    logout(undefined, {
      onSuccess: () => {
        setOpened(false);
        login({ email, password });
      },
    });
  };

  return (
    <Box
      style={{
        position: 'fixed',
        bottom: 16,
        left: 16,
        zIndex: 99_999,
      }}
    >
      {/* Slide-up panel from bottom edge */}
      <Box
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: '#0d1117',
          borderTop: '1px solid rgba(255, 255, 255, 0.12)',
          boxShadow: '0 -8px 32px rgba(0, 0, 0, 0.4)',
          transform: opened ? 'translateY(0)' : 'translateY(100%)',
          transition: 'transform 300ms ease-out',
          pointerEvents: opened ? 'auto' : 'none',
          zIndex: 99_998,
        }}
      >
        <Box p="md" style={{ maxWidth: 600, margin: '0 auto' }}>
          {isLoggedIn ? (
            <Stack gap="sm">
              <Box>
                <Text c="rgba(255,255,255,0.4)" ff="monospace" fw={700} size="10px" tt="uppercase">
                  Logged in as
                </Text>
                <Text c="white" ff="monospace" fw={600} mt={2} size="sm">
                  {user.email}
                </Text>
              </Box>
              <Box style={{ height: 1, backgroundColor: 'rgba(255,255,255,0.08)' }} />
              <AccountButton description="End current session" label="Logout" onClick={handleLogout} />
              <Box>
                <Text c="rgba(255,255,255,0.4)" ff="monospace" fw={700} mb="xs" size="10px" tt="uppercase">
                  Switch to
                </Text>
                <Stack gap="xs">
                  {DEV_ACCOUNTS.filter((a) => a.email !== user.email).map((account) => (
                    <AccountButton
                      description={account.description}
                      key={account.email}
                      label={account.label}
                      onClick={() => handleSwitchAccount(account.email, account.password)}
                    />
                  ))}
                </Stack>
              </Box>
            </Stack>
          ) : (
            <Stack gap="sm">
              <Text c="rgba(255,255,255,0.4)" ff="monospace" fw={700} size="10px" tt="uppercase">
                Quick Login
              </Text>
              <Stack gap="xs">
                {DEV_ACCOUNTS.map((account) => (
                  <AccountButton
                    description={account.description}
                    key={account.email}
                    label={account.label}
                    onClick={() => handleLogin(account.email, account.password)}
                  />
                ))}
              </Stack>
            </Stack>
          )}
        </Box>
      </Box>

      {/* Circle toggle button */}
      <UnstyledButton
        style={{
          width: 48,
          height: 48,
          borderRadius: '50%',
          backgroundColor: opened ? '#1a2332' : '#0d1117',
          border: `1.5px solid ${opened ? '#00C853' : 'rgba(255, 255, 255, 0.15)'}`,
          cursor: 'pointer',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
          transition: 'border-color 150ms, background-color 150ms, box-shadow 150ms',
        }}
        onClick={() => setOpened((o) => !o)}
        onMouseEnter={(e) => {
          if (!opened) {
            e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
            e.currentTarget.style.boxShadow = '0 2px 12px rgba(0, 0, 0, 0.4)';
          }
        }}
        onMouseLeave={(e) => {
          if (!opened) {
            e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.15)';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.3)';
          }
        }}
      >
        <DevTriangle size={16} />
        <Text c={opened ? '#00C853' : 'rgba(255,255,255,0.6)'} ff="monospace" fw={700} lh={1} size="8px">
          DEV
        </Text>
      </UnstyledButton>
    </Box>
  );
};
